import classNames from 'classnames';
import React, { useEffect, useId, useState } from 'react';

import { useSubmitForm } from './useSubmitForm';
import Button from '../../../components/Button';
import IconButton from '../../../components/IconButton';
import Caret from '../../../components/icons/Caret';
import RawHtml from '../../../components/RawHtml';
import { recaptchaInputId } from '../GravityFormsFields/CaptchaInput';
import ErrorMessage from '../GravityFormsFields/ErrorMessage';
import { mapFormFieldsToInputs } from '../GravityFormsFields/helpers';
import { getFieldValues, mapFormFieldsToStateFields } from '../helpers';
import { GravityFormsState } from '../state/GravityFormsProvider';
import { useGetFormErrors, useGravityFormsDispatch, useGravityFormsState } from '../state/hooks';
import { GravityFormFragmentForm, GravityFormFragmentFormFieldNodes } from '../types';

import style from './gravity-forms-form.module.css';

type NodeWithPageNumber = Extract<GravityFormFragmentFormFieldNodes[number], { pageNumber?: null | number }>;

interface Props {
  disclaimer?: null | string;
  form: GravityFormFragmentForm;
  onSuccess?: () => void;
}

const hasPageNumber = (node: GravityFormFragmentFormFieldNodes[number]): node is NodeWithPageNumber => {
  return 'pageNumber' in node;
};

const filterNodes = (nodes: GravityFormFragmentFormFieldNodes): NodeWithPageNumber[] => {
  return nodes.filter(hasPageNumber);
};

const GravityFormsForm = ({
  disclaimer,
  form,
  onSuccess,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [changeDirection, setChangeDirection] = useState<'next' | 'previous' | null>(null);
  const [allPagesVisible, setAllPagesVisible] = useState(false);
  const [shouldNavigateToErrorPage, setShouldNavigateToErrorPage] = useState(false);

  const [submitForm, {
    data,
    error,
    loading,
  }] = useSubmitForm();

  const formFields = form.formFields?.nodes;
  const state = useGravityFormsState();
  const dispatch = useGravityFormsDispatch();
  const submitButtonText = form.submitButton?.text ?? 'Submit';

  const id = useId();

  useEffect(() => {
    dispatch({
      payload: {
        state: mapFormFieldsToStateFields(formFields ?? []),
      },
      type: 'initializeFields',
    });
  }, [
    dispatch,
    formFields,
  ]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loading) {
      return;
    }

    const recaptchaInput = document.getElementById(recaptchaInputId) as HTMLInputElement | null;
    if (recaptchaInput && !recaptchaInput.value) {
      const databaseId = parseInt(recaptchaInput.dataset.recaptchaDatabaseId ?? '0');
      dispatch({
        payload: {
          errors: {
            [databaseId]: [
              'The reCAPTCHA was invalid. Go back and try it again.',
            ],
          },
        },
        type: 'updateErrors',
      });

      return;
    }

    // this will allow the browser to focus on the first invalid field
    setAllPagesVisible(true);
    setShouldNavigateToErrorPage(true);

    submitForm({
      onCompleted: onSuccess,
      variables: {
        fieldValues: getFieldValues(state),
        formId: form.databaseId.toString(),
      },
    }).catch((e: unknown) => {
      // TODO: add error handling
      console.error(e);
    });
  }

  useEffect(() => {
    setAllPagesVisible(false);
    setShouldNavigateToErrorPage(false);
  }, [currentPage]);

  useEffect(() => {
    dispatch({
      payload: {
        errors: (data?.submitGfForm?.errors ?? []).reduce<GravityFormsState['errors']>((prev, curr) => {
          const fieldId = curr?.id;

          if (!fieldId) {
            return prev;
          }

          prev[fieldId] ??= [];
          prev[fieldId].push(curr.message ?? '');

          return prev;
        }, {}),
      },
      type: 'updateErrors',
    });
  }, [data?.submitGfForm?.errors, dispatch]);

  const fieldsByPage = Object.groupBy(filterNodes(formFields ?? []), field => field.pageNumber ?? 1);
  const totalPages = Object.keys(fieldsByPage).length;

  const hasErrorsOnCurrentPage = fieldsByPage[currentPage]?.some(field => {
    return !!state.errors[field.databaseId]?.length;
  });

  const formErrors = useGetFormErrors();

  useEffect(() => {
    const hasErrors = Object.values(formErrors).some(e => !!e?.length);

    if (hasErrors) {
      const firstPage = Object.entries(fieldsByPage).find(({ 1: fields }) => {
        return fields?.some(field => !!formErrors[field.databaseId]?.length);
      });

      if (!firstPage) {
        return;
      }

      const [pageToNavigateTo] = firstPage;

      if (pageToNavigateTo && shouldNavigateToErrorPage) {
        setCurrentPage(parseInt(pageToNavigateTo));
        setChangeDirection('previous');
      }
    }
  }, [
    formErrors,
    fieldsByPage,
    shouldNavigateToErrorPage,
  ]);

  const handleNext = () => {
    const formElement = document.getElementById(id);

    const dataPageSelector = `[data-page="${currentPage.toString()}"]`;
    const currentPageElement = formElement?.querySelector(dataPageSelector);

    const invalidFields = currentPageElement?.querySelectorAll('input:invalid, textarea:invalid, select:invalid');

    if (invalidFields?.length) {
      // Show the first invalid field
      (invalidFields[0] as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)).reportValidity();

      return;
    }

    if (currentPage < totalPages) {
      setChangeDirection('next');
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setChangeDirection('previous');
      setCurrentPage(prev => prev - 1);
    }
  };

  return (
    <form
      className={style.form}
      id={id}
      onSubmit={handleSubmit}
    >
      {Object.entries(fieldsByPage).map(([pageNumber, fields]) => {
        if (fields) {
          const parsedPageNumber = parseInt(pageNumber);

          return (
            <div
              className={classNames(
                style.formPage,
                {
                  [style.previous]: parsedPageNumber < currentPage,
                  [style.current]: parsedPageNumber === currentPage,
                  [style.next]: parsedPageNumber > currentPage,
                  [style.fromNext]: changeDirection === 'next',
                  [style.fromPrevious]: changeDirection === 'previous',
                  [style.formPageVisible]: allPagesVisible,
                },
              )}
              data-page={pageNumber}
              key={pageNumber}
            >
              <div className={style.formPageFields}>
                {fields.map(mapFormFieldsToInputs)}
              </div>

              {disclaimer && (
                <RawHtml>
                  {disclaimer}
                </RawHtml>
              )}

              <div className={style.formButtons}>
                {parsedPageNumber > 1 && (
                  <IconButton
                    aria-label="Previous page"
                    className={style.previousButton}
                    onClick={handlePrevious}
                    round
                    type="button"
                  >
                    <Caret />
                  </IconButton>
                )}

                {parsedPageNumber < totalPages && (
                  <Button
                    disabled={hasErrorsOnCurrentPage}
                    onClick={handleNext}
                    type="button"
                  >
                    Next
                  </Button>
                )}

                {parsedPageNumber === totalPages && (
                  <Button
                    buttonColor="orange"
                    hideCaret
                    isLoading={loading}
                    type="submit"
                  >
                    {submitButtonText}
                  </Button>
                )}
              </div>
            </div>
          );
        }
      })}

      {error && (
        <ErrorMessage className={style.errorMessage}>
          <RawHtml>
            {error.message}
          </RawHtml>
        </ErrorMessage>
      )}
    </form>
  );
};

export default GravityFormsForm;
